import { put, takeLatest, call } from 'redux-saga/effects';
import axios from 'axios';
import Api from 'guardian/reducers/Api';

// Action Types
const FETCH_SCANS = 'guardian/scans/FETCH_SCANS';
const FETCH_SCANS_SUCCESS = 'guardian/scans/FETCH_SCANS_SUCCESS';
const FETCH_SCANS_FAILURE = 'guardian/scans/FETCH_SCANS_FAILURE';
const FETCH_SCAN = 'guardian/scans/FETCH_SCAN';
const FETCH_SCAN_SUCCESS = 'guardian/scans/FETCH_SCAN_SUCCESS';
const FETCH_SCAN_FAILURE = 'guardian/scans/FETCH_SCAN_FAILURE';
const SET_FILTERS = 'guardian/scans/SET_FILTERS';
// New action types (Batch 2 / BNWAS-19863)
const SET_RESULTS = 'guardian/scans/SET_RESULTS';
const SET_CURRENT = 'guardian/scans/SET_CURRENT';
const SET_LOADING = 'guardian/scans/SET_LOADING';
const SET_ERROR = 'guardian/scans/SET_ERROR';

// Named action creator exports (importable directly in tests and new hooks)
export const fetchScans = (payload) => ({ type: FETCH_SCANS, payload });
export const fetchScan = (correlationId) => ({ type: FETCH_SCAN, payload: correlationId });
export const setResults = ({ results, count }) => ({ type: SET_RESULTS, payload: { results, count } });
export const setCurrent = (scan) => ({ type: SET_CURRENT, payload: scan });
export const setLoading = (v) => ({ type: SET_LOADING, payload: v });
export const setError = (err) => ({ type: SET_ERROR, payload: err });
const setFilters = (filters) => ({ type: SET_FILTERS, payload: filters });

// Action Creators object — kept for backward compat with ScansContainer and other existing consumers
export const actions = {
  fetchScans,
  fetchScan,
  setFilters,
  setResults,
  setCurrent,
  setLoading,
  setError,
};

// Initial State — page/pageSize are new; filters kept for backward compat
const initialState = {
  list: [],
  count: 0,
  current: null,
  loading: false,
  error: null,
  filters: {},   // KEEP — ScansContainer reads state.guardian.scans.filters
  page: 1,       // NEW
  pageSize: 50,  // NEW
};

// Reducer
export default function scansReducer(state = initialState, action) {
  switch (action.type) {
    case FETCH_SCANS:
    case FETCH_SCAN:
      return { ...state, loading: true, error: null };
    case FETCH_SCANS_SUCCESS:
      return { ...state, loading: false, list: action.payload.results, count: action.payload.count };
    case FETCH_SCAN_SUCCESS:
      return { ...state, loading: false, current: action.payload };
    case FETCH_SCANS_FAILURE:
    case FETCH_SCAN_FAILURE:
      return { ...state, loading: false, error: action.payload };
    case SET_FILTERS:
      return { ...state, filters: action.payload };
    // New cases (alias / complement the legacy SUCCESS/FAILURE cases)
    case SET_RESULTS:
      return { ...state, list: action.payload.results, count: action.payload.count, loading: false, error: null };
    case SET_CURRENT:
      return { ...state, current: action.payload, loading: false };
    case SET_LOADING:
      return { ...state, loading: action.payload };
    case SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    default:
      return state;
  }
}

// --- Saga helpers ---

// Range preset → milliseconds
const RANGE_MS = {
  '15m': 15 * 60_000,
  '1h': 60 * 60_000,
  '24h': 24 * 60 * 60_000,
  '7d': 7 * 24 * 60 * 60_000,
  '30d': 30 * 24 * 60 * 60_000,
};

/**
 * Build a URLSearchParams query string from the new frontend filter shape.
 * Multi-value params (account_id, action, categories, provider, severity) are
 * appended individually so the backend receives repeated flat keys — e.g.
 * ?action=block&action=partial.  URLSearchParams.toString() produces that format
 * natively.
 *
 * Exported for unit testing.
 */
export function buildQueryParams(filters) {
  const p = new URLSearchParams();
  if (!filters || typeof filters !== 'object') return p.toString();

  const { range, from, to } = filters;
  if (range === 'custom' && from && to) {
    p.set('from_date', from);
    p.set('to_date', to);
  } else if (range && RANGE_MS[range]) {
    const now = new Date();
    p.set('from_date', new Date(now.getTime() - RANGE_MS[range]).toISOString());
    p.set('to_date', now.toISOString());
  }

  if (filters.apiKeyIds?.length) filters.apiKeyIds.forEach((v) => p.append('account_id', v));
  if (filters.actions?.length) filters.actions.forEach((v) => p.append('action', v));
  if (filters.categories?.length) filters.categories.forEach((v) => p.append('categories', v));
  if (filters.correlationId) p.set('correlation_id', filters.correlationId);
  if (filters.status) p.set('status', filters.status);
  if (filters.tenantId) p.set('tenant_id', filters.tenantId);
  if (filters.userId) p.set('user_id', filters.userId);
  if (filters.contentType) p.set('content_type', filters.contentType);
  // Phase 2 filter params
  if (filters.providers?.length) filters.providers.forEach((v) => p.append('provider', v));
  if (filters.severities?.length) filters.severities.forEach((v) => p.append('severity', v));
  if (filters.minConfidence != null && filters.minConfidence > 0) {
    p.set('min_confidence', String(filters.minConfidence));
  }
  // page/pageSize omit defaults to avoid polluting the query string
  if (filters.page && filters.page !== 1) p.set('page', String(filters.page));
  if (filters.pageSize && filters.pageSize !== 50) p.set('page_size', String(filters.pageSize));

  return p.toString();
}

// --- Saga workers ---

/**
 * fetchScansSaga — handles both payload shapes:
 *   Legacy (old ScansContainer):  fetchScans(rawBackendParams)
 *     → payload is a plain object with backend-named keys (status, tenant_id, from_date, to_date)
 *     → delegated directly to Api.fetchScans for axios param serialization
 *
 *   New (redesigned ScansPage):   fetchScans({ filters, page, pageSize })
 *     → payload.filters uses frontend camelCase keys
 *     → converted to a query string via buildQueryParams + URLSearchParams
 */
export function* fetchScansSaga(action) {
  try {
    const payload = action.payload;
    const isNewShape = payload && typeof payload === 'object' && 'filters' in payload;

    if (isNewShape) {
      const { filters, page, pageSize } = payload;
      // Top-level page/pageSize override filters.* when explicitly provided.
      // When absent (dispatch({ filters }) — the common case from ScansPage),
      // fall back to filters.page / filters.pageSize so URL-driven paging works.
      const qs = buildQueryParams({
        ...filters,
        page: page ?? filters?.page,
        pageSize: pageSize ?? filters?.pageSize,
      });
      const resp = yield call(axios.get, `api/v1/scans/${qs ? '?' + qs : ''}`);
      yield put(setResults({ results: resp.data.results, count: resp.data.count }));
    } else {
      // Legacy path — pass backend-named params directly to Api.fetchScans
      const response = yield call(Api.fetchScans, payload);
      // Dispatch both for backward compat (FETCH_SCANS_SUCCESS) and new consumers (SET_RESULTS)
      yield put({ type: FETCH_SCANS_SUCCESS, payload: response.data });
      yield put(setResults({ results: response.data.results, count: response.data.count }));
    }
  } catch (error) {
    yield put({ type: FETCH_SCANS_FAILURE, payload: error.message });
    yield put(setError(error.message));
  }
}

function* fetchScanSaga(action) {
  try {
    const response = yield call(Api.fetchScan, action.payload);
    yield put(setCurrent(response.data));
    // Also keep legacy action alive so any existing FETCH_SCAN_SUCCESS listeners still work
    yield put({ type: FETCH_SCAN_SUCCESS, payload: response.data });
  } catch (error) {
    yield put({ type: FETCH_SCAN_FAILURE, payload: error.message });
    yield put(setError(error.message));
  }
}

export function* scansSagas() {
  yield takeLatest(FETCH_SCANS, fetchScansSaga);
  yield takeLatest(FETCH_SCAN, fetchScanSaga);
}
